<template>
  <div class="st-offers-view">
    <StHeadline>Specialerbjudande</StHeadline>
    <div class="st-offers-view__search">
      <QuickSearch
        v-model="searchQuery"
        @search="search"
        label-name="Sök på organisationsnummer eller erbjudandenamn"
        allow-empty-search
      />
    </div>
    <div class="st-offers-view__actions">
      <StButton
        rectangular
        @click="openAddOfferPanel"
        :disabled="!userState.isAdmin"
      >
        <StIcon name="st-plus-small" />
        Lägg till
      </StButton>
    </div>
    <EmptyList
      v-if="hasSearched && !offers.length"
      description="Vi hittade inga specialerbjudande som matchade dina sökkriterier. Testa att söka med en annan sökterm."
    />
    <StError v-if="errorMessage" :error-message="errorMessage" />
    <StSpinner v-else-if="isLoading" loading-message="Hämtar resultat..." />
    <StPagination
      v-else-if="offers.length"
      v-slot="{ paginatedData }"
      :data-to-paginate="paginatedTableContent"
      :rows-per-page="RowsPerPage"
      :total-number-of-pages="totalPages"
      :override-current-page-to="currentPage"
      :label="`${offers.length} träffar`"
      @change-page="currentPage = $event"
    >
      <StTable
        :headers="tableHeaders"
        :table-data="paginatedData"
        @sort="handleSort"
      />
    </StPagination>
    <AddOfferPanel
      ref="addOfferPanel"
      :offers="managedOffers"
      :is-loading="addOfferValidationIsLoading"
      :error-message="addOfferValidationErrorMessage"
      @add="confirmAddOffer"
      @close="closeAddOfferPanel"
    />
    <AddOfferConfirmationModal
      ref="addOfferConfirmationModal"
      :offer-name="pendingAddOffer.offerName"
      :organization-number="pendingAddOffer.organizationNumber"
      :is-loading="addOfferIsLoading"
      :error-message="addOfferErrorMessage"
      @add="addOffer"
      @close="closeAddOfferConfirmation"
    />
    <ConfirmationModal
      ref="confirmationModal"
      title="Specialerbjudande tillagt."
      status
      @close="closeConfirmationModal"
    />
  </div>
</template>
<script setup>
import {
  StButton,
  StError,
  StHeadline,
  StIcon,
  StPagination,
  StSpinner,
  StTable,
} from "skanetrafiken";
import QuickSearch from "@/components/QuickSearch.vue";
import EmptyList from "@/components/common/EmptyList.vue";
import AddOfferPanel from "@/components/AddOfferPanel.vue";
import AddOfferConfirmationModal from "@/components/modals/AddOfferConfirmationModal.vue";
import ConfirmationModal from "@/components/modals/ConfirmationModal.vue";
import { useOffersTable } from "@/composables/tables/useOffersTable";
import { useModal } from "@/composables/useModal";
import { computed, ref, onMounted } from "vue";
import Services from "@/services";
import { GENERIC_ERROR_MSG } from "@/constants";
import { useUser } from "@/composables/useUser";

const { state: userState } = useUser();

const offers = ref([]);
const managedOffers = ref([]);
const isLoading = ref(false);
const searchQuery = ref("");
const hasSearched = ref(false);
const errorMessage = ref(null);
const addOfferIsLoading = ref(false);
const addOfferErrorMessage = ref("");
const addOfferValidationIsLoading = ref(false);
const addOfferValidationErrorMessage = ref("");
const addOfferPanel = ref(null);
const addOfferConfirmationModal = ref(null);
const confirmationModal = ref(null);
const pendingAddOffer = ref({});
const currentPage = ref(1);
const RowsPerPage = 10;
const DUPLICATE_OFFER_ERROR_MSG =
  "Specialerbjudandet finns redan för organisationen. Ändra det befintliga erbjudandet i stället.";
const [openAddOfferPanelModal, closeAddOfferPanelModal] =
  useModal(addOfferPanel);
const [openAddOfferConfirmationModal, closeAddOfferConfirmationModal] =
  useModal(addOfferConfirmationModal);
const [openConfirmationModal, closeConfirmationModal] =
  useModal(confirmationModal);

const removeOffer = (offer) => {
  offers.value = offers.value.filter(
    ({ accountOfferId }) => accountOfferId !== offer.accountOfferId,
  );
  currentPage.value = Math.min(
    currentPage.value,
    Math.max(1, Math.ceil(offers.value.length / RowsPerPage)),
  );
};

const updateOffer = (updatedOffer) => {
  offers.value = offers.value.map((offer) =>
    offer.accountOfferId === updatedOffer.accountOfferId ? updatedOffer : offer,
  );
};

const table = useOffersTable(removeOffer, updateOffer);
const tableHeaders = table.getTableHeaders();
const tableContent = computed(() => table.getTableContent(offers.value));
const totalPages = computed(() =>
  Math.ceil(tableContent.value.length / RowsPerPage),
);
const paginatedTableContent = computed(() => {
  const start = (currentPage.value - 1) * RowsPerPage;
  return tableContent.value.slice(start, start + RowsPerPage);
});

onMounted(async () => {
  isLoading.value = true;

  try {
    const [offerResults, managedOfferResults] = await Promise.all([
      Services.Offers.POST.SEARCH(null),
      Services.Offers.GET.MANAGED_OFFERS(),
    ]);
    offers.value = offerResults;
    handleSort("created", "desc");
    managedOffers.value = managedOfferResults;
    hasSearched.value = true;
  } catch (err) {
    errorMessage.value = GENERIC_ERROR_MSG;
  } finally {
    isLoading.value = false;
  }
});

const handleSort = (sortBy, order) => {
  offers.value = table.sortTable([...offers.value], sortBy, order);
};

const openAddOfferPanel = () => {
  addOfferValidationErrorMessage.value = "";
  openAddOfferPanelModal();
};

const closeAddOfferPanel = () => {
  addOfferValidationErrorMessage.value = "";
  closeAddOfferPanelModal();
};

const confirmAddOffer = async (payload) => {
  const managedOffer = managedOffers.value.find(
    ({ id }) => id === payload.offerId,
  );
  addOfferValidationIsLoading.value = true;
  addOfferValidationErrorMessage.value = "";

  try {
    const existingOffers = await Services.Offers.POST.SEARCH(
      payload.organizationNumber,
    );
    const normalizeOrganizationNumber = (organizationNumber) =>
      organizationNumber?.replace(/-/g, "").trim();
    const duplicateOffer = existingOffers.some(
      (offer) =>
        normalizeOrganizationNumber(offer.organizationNumber) ===
          normalizeOrganizationNumber(payload.organizationNumber) &&
        Number(offer.offerId) === payload.offerId,
    );

    if (duplicateOffer) {
      addOfferValidationErrorMessage.value = DUPLICATE_OFFER_ERROR_MSG;
      return;
    }

    pendingAddOffer.value = {
      ...payload,
      offerName: managedOffer?.name || "",
    };
    closeAddOfferPanel();
    openAddOfferConfirmationModal();
  } catch (err) {
    addOfferValidationErrorMessage.value = GENERIC_ERROR_MSG;
  } finally {
    addOfferValidationIsLoading.value = false;
  }
};

const closeAddOfferConfirmation = () => {
  closeAddOfferConfirmationModal();
  addOfferErrorMessage.value = "";
  pendingAddOffer.value = {};
};

const addOffer = async () => {
  addOfferIsLoading.value = true;
  addOfferErrorMessage.value = "";

  try {
    await Services.Offers.POST.ADD_ACCOUNT_OFFER({
      offerId: pendingAddOffer.value.offerId,
      subscription: pendingAddOffer.value.subscription,
      organizationNumber: pendingAddOffer.value.organizationNumber,
    });
    offers.value = await Services.Offers.POST.SEARCH(searchQuery.value);
    handleSort("created", "desc");
    closeAddOfferConfirmation();
    openConfirmationModal();
  } catch (err) {
    if (err.response.data.errorCode === "MO0001") {
      addOfferErrorMessage.value =
        "Denna organisation finns inte i vårt system. Vänligen ändra organisationsnumret och försök igen.";
    } else {
      addOfferErrorMessage.value = GENERIC_ERROR_MSG;
    }
  } finally {
    addOfferIsLoading.value = false;
  }
};

const search = async () => {
  isLoading.value = true;

  try {
    offers.value = await Services.Offers.POST.SEARCH(searchQuery.value);
    handleSort("created", "desc");
    currentPage.value = 1;
    hasSearched.value = true;
  } catch (err) {
    errorMessage.value = GENERIC_ERROR_MSG;
  } finally {
    isLoading.value = false;
  }
};
</script>

<style scoped lang="scss">
@import "~skanetrafiken/dist/scss/main.scss";

.st-offers-view {
  padding: rem-calc(24);
  text-align: left;

  &__search {
    width: 100%;
    margin-bottom: rem-calc(36);
    margin-top: rem-calc(16);
    display: flex;

    @include medium {
      width: 50%;
    }
  }

  &__actions {
    display: flex;
    justify-content: flex-start;
    margin-bottom: rem-calc(16);

    @include medium {
      justify-content: flex-end;
    }
  }
}
</style>
